/**
 * Monthly returns heatmap showing returns by month (columns) and year (rows).
 * Uses color gradient: red for negative returns, green for positive, neutral for zero.
 * Includes row totals (annual returns) and column averages (monthly averages).
 */
import type { MonthlyReturn } from '@/types/performance';
import { calculateMonthlyAverages } from '@/utils/performanceCalculations';
import { formatCurrency, formatPercent } from '@/utils/formatters';

interface MonthlyReturnsHeatmapProps {
  data: MonthlyReturn[];
  isLoading?: boolean;
}

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Get background color style for a cell based on returnPercent.
 * Negative: red tones, Positive: green tones, Zero/neutral: bg-dark-700.
 * Opacity scales with magnitude, capped at 10%.
 */
function getCellStyle(returnPercent: number): React.CSSProperties {
  if (returnPercent === 0) {
    return { backgroundColor: 'rgb(51 65 85)' }; // dark-700
  }

  const opacity = Math.min(Math.abs(returnPercent) / 10, 1);

  if (returnPercent < 0) {
    // Rose-500: rgb(244 63 94)
    return { backgroundColor: `rgba(244, 63, 94, ${opacity})` };
  }

  // Emerald-500: rgb(16 185 129)
  return { backgroundColor: `rgba(16, 185, 129, ${opacity})` };
}

/**
 * Get text color class based on return value.
 */
function getTextColor(returnPercent: number): string {
  if (returnPercent > 0) return 'text-emerald-400';
  if (returnPercent < 0) return 'text-rose-400';
  return 'text-dark-400';
}

/**
 * Calculate annual return by compounding monthly returns for a given year.
 * Formula: ((1 + r1/100) * (1 + r2/100) * ... - 1) * 100
 */
function calculateAnnualReturn(monthlyData: MonthlyReturn[]): {
  returnPercent: number;
  returnDollar: number;
} {
  if (monthlyData.length === 0) {
    return { returnPercent: 0, returnDollar: 0 };
  }

  const compoundedReturn = monthlyData.reduce(
    (acc, m) => acc * (1 + m.returnPercent / 100),
    1
  );

  const returnDollar = monthlyData.reduce((sum, m) => sum + m.returnDollar, 0);

  return {
    returnPercent: (compoundedReturn - 1) * 100,
    returnDollar,
  };
}

export function MonthlyReturnsHeatmap({
  data,
  isLoading = false,
}: MonthlyReturnsHeatmapProps) {
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="heatmap-skeleton">
        {/* Header row skeleton */}
        <div className="flex gap-1">
          <div className="h-6 w-12 rounded shimmer" />
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-6 w-14 rounded shimmer" />
          ))}
          <div className="h-6 w-14 rounded shimmer" />
        </div>
        {/* Data rows skeleton */}
        {Array.from({ length: 3 }).map((_, row) => (
          <div key={row} className="flex gap-1">
            <div className="h-8 w-12 rounded shimmer" />
            {Array.from({ length: 12 }).map((_, col) => (
              <div key={col} className="h-8 w-14 rounded shimmer" />
            ))}
            <div className="h-8 w-14 rounded shimmer" />
          </div>
        ))}
        {/* Averages row skeleton */}
        <div className="flex gap-1">
          <div className="h-6 w-12 rounded shimmer" />
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-6 w-14 rounded shimmer" />
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-dark-700 bg-dark-800/50">
        <p className="text-sm text-dark-500">No monthly returns data available</p>
      </div>
    );
  }

  // Group data by year
  const yearMap = new Map<number, Map<number, MonthlyReturn>>();
  for (const entry of data) {
    if (!yearMap.has(entry.year)) {
      yearMap.set(entry.year, new Map());
    }
    yearMap.get(entry.year)!.set(entry.month, entry);
  }

  const years = Array.from(yearMap.keys()).sort((a, b) => a - b);
  const monthlyAverages = calculateMonthlyAverages(data);

  return (
    <div>
      {/* Visual heatmap grid */}
      <div
        className="overflow-x-auto"
        role="img"
        aria-label="Monthly returns heatmap"
      >
        <div className="min-w-[700px]">
          {/* Header row */}
          <div className="mb-1 grid grid-cols-[60px_repeat(12,1fr)_80px] gap-1">
            <div /> {/* Empty corner cell */}
            {MONTH_LABELS.map((month) => (
              <div
                key={month}
                className="text-center text-xs font-medium text-dark-400"
              >
                {month}
              </div>
            ))}
            <div className="text-center text-xs font-medium text-dark-400">
              Annual
            </div>
          </div>

          {/* Data rows */}
          {years.map((year) => {
            const yearData = yearMap.get(year)!;
            const yearEntries = data.filter((d) => d.year === year);
            const annual = calculateAnnualReturn(yearEntries);

            return (
              <div
                key={year}
                className="mb-1 grid grid-cols-[60px_repeat(12,1fr)_80px] gap-1"
              >
                {/* Year label */}
                <div className="flex items-center text-sm font-medium text-dark-200">
                  {year}
                </div>

                {/* Month cells */}
                {Array.from({ length: 12 }).map((_, monthIndex) => {
                  const month = monthIndex + 1;
                  const entry = yearData.get(month);

                  if (!entry) {
                    return (
                      <div
                        key={month}
                        className="rounded-md bg-dark-800 p-2 text-center text-xs text-dark-600"
                      >
                        —
                      </div>
                    );
                  }

                  return (
                    <div
                      key={month}
                      className={`rounded-md p-2 text-center text-xs font-medium ${getTextColor(entry.returnPercent)}`}
                      style={getCellStyle(entry.returnPercent)}
                      title={`${MONTH_LABELS[monthIndex]} ${year}: ${formatPercent(entry.returnPercent)} (${formatCurrency(entry.returnDollar)})`}
                    >
                      {entry.returnPercent > 0 ? '+' : ''}
                      {entry.returnPercent.toFixed(1)}%
                    </div>
                  );
                })}

                {/* Annual total */}
                <div
                  className={`rounded-md border border-dark-600 p-2 text-center text-xs font-medium ${getTextColor(annual.returnPercent)}`}
                  title={`${year} Annual: ${formatPercent(annual.returnPercent)} (${formatCurrency(annual.returnDollar)})`}
                >
                  {annual.returnPercent > 0 ? '+' : ''}
                  {annual.returnPercent.toFixed(1)}%
                </div>
              </div>
            );
          })}

          {/* Averages row */}
          <div className="mt-2 grid grid-cols-[60px_repeat(12,1fr)_80px] gap-1 border-t border-dark-700 pt-2">
            <div className="flex items-center text-xs font-medium text-dark-400">
              Avg
            </div>
            {monthlyAverages.map((avg, i) => (
              <div
                key={i}
                className={`rounded-md p-2 text-center text-xs font-medium ${getTextColor(avg)}`}
                title={`${MONTH_LABELS[i]} Average: ${formatPercent(avg)}`}
              >
                {avg > 0 ? '+' : ''}
                {avg.toFixed(1)}%
              </div>
            ))}
            <div /> {/* Empty cell for annual column */}
          </div>
        </div>
      </div>

      {/* Accessible sr-only data table */}
      <table className="sr-only">
        <caption>Monthly returns by year</caption>
        <thead>
          <tr>
            <th>Year</th>
            {MONTH_LABELS.map((month) => (
              <th key={month}>{month}</th>
            ))}
            <th>Annual Return</th>
          </tr>
        </thead>
        <tbody>
          {years.map((year) => {
            const yearData = yearMap.get(year)!;
            const yearEntries = data.filter((d) => d.year === year);
            const annual = calculateAnnualReturn(yearEntries);

            return (
              <tr key={year}>
                <td>{year}</td>
                {Array.from({ length: 12 }).map((_, monthIndex) => {
                  const entry = yearData.get(monthIndex + 1);
                  return (
                    <td key={monthIndex}>
                      {entry
                        ? `${formatPercent(entry.returnPercent)} (${formatCurrency(entry.returnDollar)})`
                        : 'N/A'}
                    </td>
                  );
                })}
                <td>
                  {formatPercent(annual.returnPercent)} ({formatCurrency(annual.returnDollar)})
                </td>
              </tr>
            );
          })}
          <tr>
            <td>Average</td>
            {monthlyAverages.map((avg, i) => (
              <td key={i}>{formatPercent(avg)}</td>
            ))}
            <td>N/A</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
